import { useCallback, useEffect, useRef, useState } from "react";
import downloadCsv from "../../../utils/downloadCsv";

const DEFAULT_DEBOUNCE_MS = 600;

/**
 * Shared data handling for the widgets on the Report page.
 *
 * The report is built at most once per `cacheKey` (the selected date or date
 * range) and the resulting CSV is kept until that key changes:
 *
 * - `scheduleLoad()` starts a debounced load and belongs on the date inputs'
 *   onChange, so stepping through dates costs one request instead of one per step.
 * - `loadNow()` loads without waiting for the debounce and belongs on their onBlur.
 * - `download()` saves the cached CSV, loading it first if the cache misses.
 *
 * Every load is keyed, so a response for a date range the user has already moved
 * away from can never end up in the downloaded file.
 */
const useReportData = ({
  cacheKey,
  isReady,
  loadCsv,
  fileName,
  infoMessage,
  emptyMessage,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [failureReason, setFailureReason] = useState("");
  const [info, setInfo] = useState("");
  const [result, setResult] = useState(null);

  const timerRef = useRef(null);
  const pendingRef = useRef(null);
  const isMountedRef = useRef(true);
  const latestRef = useRef({});
  latestRef.current = { cacheKey, isReady, loadCsv, fileName, infoMessage, emptyMessage, result };

  useEffect(
    () => () => {
      isMountedRef.current = false;
      clearTimeout(timerRef.current);
    },
    []
  );

  const load = useCallback((key) => {
    const latest = latestRef.current;
    if (latest.result?.key === key) {
      return Promise.resolve(latest.result);
    }
    if (pendingRef.current?.key === key) {
      return pendingRef.current.promise;
    }
    // Any load still in flight is for another key and is left to finish; the
    // key check below stops it from touching the state of this one. The slot is
    // claimed before the work starts so that the check holds even if `loadCsv`
    // throws synchronously.
    const pending = { key, promise: null };
    pendingRef.current = pending;
    setFailureReason("");
    setIsLoading(true);
    setInfo(latest.infoMessage);
    pending.promise = (async () => {
      try {
        const { csv, rowCount } = (await latest.loadCsv()) || {};
        const loaded = { key, csv, rowCount: rowCount || 0 };
        if (isMountedRef.current && pendingRef.current?.key === key) {
          setResult(loaded);
          setFailureReason(loaded.rowCount > 0 ? "" : latest.emptyMessage);
        }
        return loaded;
      } catch (error) {
        console.error(error);
        if (isMountedRef.current && pendingRef.current?.key === key) {
          setResult(null);
          setFailureReason(`${error}`);
        }
        return null;
      } finally {
        if (pendingRef.current?.key === key) {
          pendingRef.current = null;
          if (isMountedRef.current) {
            setIsLoading(false);
            setInfo("");
          }
        }
      }
    })();
    return pending.promise;
  }, []);

  // A widget that already has a valid date on mount (the single date ones
  // default to today) loads once straight away, so its report is ready to
  // save without the user having to touch the date first.
  useEffect(() => {
    const latest = latestRef.current;
    if (latest.isReady) {
      load(latest.cacheKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleLoad = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const latest = latestRef.current;
      if (latest.isReady) {
        load(latest.cacheKey);
      }
    }, debounceMs);
  }, [load, debounceMs]);

  const loadNow = useCallback(() => {
    clearTimeout(timerRef.current);
    const latest = latestRef.current;
    if (latest.isReady) {
      load(latest.cacheKey);
    }
  }, [load]);

  const download = useCallback(async () => {
    clearTimeout(timerRef.current);
    const latest = latestRef.current;
    if (!latest.isReady) {
      return;
    }
    const key = latest.cacheKey;
    const loaded = await load(key);
    const current = latestRef.current;
    if (current.cacheKey === key && loaded?.rowCount > 0) {
      downloadCsv(loaded.csv, current.fileName);
    }
  }, [load]);

  return {
    isLoading,
    failureReason,
    info,
    isEmpty: result?.key === cacheKey && result.rowCount === 0,
    scheduleLoad,
    loadNow,
    download,
  };
};

export default useReportData;
